import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Input from 'components/forms/Input'
import CartographyPanel from 'components/map/CartographyPanel'
import ProgressBar from 'components/progress/ProgressBar'
import { useMemo } from 'react'
import { nbPerPageLabel, REFERENCIAL_TYPE_NAME } from 'referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import { getDataWithHeaders } from 'utils/ExportDataUtil'
import AdministrationAction from '../../../../administration/actions/AdministrationAction'
import Checkbox from '../../../../components/forms/Checkbox'
import Select from '../../../../components/forms/Select'
import HomeAction from '../../../../home/actions/HomeAction'
import {
    CONTACT_LINK,
    LINK_REFERENCIAL_CONTACT,
    PATH_REFERENCIAL,
    PATH_REFERENCIAL_CONTACT,
    PATH_REFERENCIAL_CONTACT_NEW,
    PATH_REFERENCIAL_RPQS_LINK,
} from '../../../../home/constants/RouteConstants'
import ReferentialAction from '../../../action/ReferencialAction'
import CityAction from '../../city/actions/CityAction'
import CountryAction from '../../country/actions/CountryAction'
import ContactAction from '../actions/ContactAction'
import { CONTACT, GROUP, MAP } from '../constants/ContactConstants'
import ContactsGroupsApp from './ContactsGroupsApp'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import { push } from '@lagunovsky/redux-react-router'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { Button, Card, CardContent, Grid, Stack } from '@mui/material'
import { CardTitle } from 'components/card/NewCard'
import { CardTable } from 'components/datatable/NewTable'
import Tabs from 'components/Tabs'

const ContactMapPanel = ({ rpqs }) => {
    const {
        contacts,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    useActions(() => ({}), [])

    useTitle(() => {
        if (rpqs) {
            return [{
                title: i18n.rpqs,
                href: 'rpqs',
            }, {
                title: i18n.RPQSReferencials,
                href: PATH_REFERENCIAL_RPQS_LINK,
            }, {
                title: i18n.map,
                href: PATH_REFERENCIAL_RPQS_LINK + CONTACT_LINK,
            }]
        }
        return [{
            title: i18n.referencials,
            href: PATH_REFERENCIAL,
        }, {
            title: i18n.map,
            href: PATH_REFERENCIAL_CONTACT,
        }]
    }, [rpqs])

    return (
        <div style={{ padding: '5px 10px' }}>
            <CartographyPanel layers={['STATIONS_POINTS']}
                componentType='all'
                stationsPoints={contacts}
                stationsPanelTitle={i18n.contacts}
                heightToSubstract={450}
            />
        </div>
    )
}

ContactMapPanel.propTypes = {
    rpqs: PropTypes.bool,
}

const ContactsPanel = ({ rpqs }) => {
    const {
        applicationSettings,
        contactsTable,
        cities,
        countries,
        referencialSandreCodes,
        globalResearch,
    } = useSelector(store => ({
        applicationSettings: store.AdministrationReducer.applicationSettings,
        contactsTable: store.ContactReducer.contactsTable,
        cities: store.CityReducer.cities,
        countries: store.CountryReducer.countries,
        referencialSandreCodes: store.ReferencialReducer.referencialSandreCodes,
        globalResearch: store.HomeReducer.globalResearch,
    }), shallowEqual)

    const [filter, setFilter] = useLocalStorage(`referencial_${REFERENCIAL_TYPE_NAME.contact}`, {})
    const [dataLoaded, setDataLoaded] = useState(false)

    const dispatch = useDispatch()

    const fetchData = () => dispatch(ContactAction.fetchContactsTable({
        ...filter,
        countryCode: filter.countrySelected,
        city: filter.citySelected?.id,
    }))

    useTitle(() => {
        if (rpqs) {
            return [{
                title: i18n.rpqs,
                href: 'rpqs',
            }, {
                title: i18n.RPQSReferencials,
                href: PATH_REFERENCIAL_RPQS_LINK,
            }, {
                title: i18n.contacts,
                href: PATH_REFERENCIAL_RPQS_LINK + CONTACT_LINK,
            }]
        }
        return [{
            title: i18n.referencials,
            href: PATH_REFERENCIAL,
        }, {
            title: i18n.contacts,
            href: PATH_REFERENCIAL_CONTACT,
        }]
    }, [rpqs])

    useEffect(() => {
        dispatch(HomeAction.setHelpLink('', ''))
        fetchData().then(() => setDataLoaded(true))

        if (!cities.length) {
            dispatch(CityAction.fetchCities())
        }
        if (!countries.length) {
            dispatch(CountryAction.fetchCountries())
        }
        if (!referencialSandreCodes.length) {
            dispatch(ReferentialAction.fetchReferencialSandreCodes())
        }
        if (globalResearch) {
            setFilter({ ...filter, searchValue: globalResearch })
            dispatch(HomeAction.updateGlobalResearch(''))
        }
        if (!applicationSettings.length) {
            dispatch(AdministrationAction.fetchApplicationSettings())
        }
        dispatch(ContactAction.fetchContacts(true))
    }, [])

    const onResetFilters = () => {
        setFilter({})
        dispatch(ContactAction.fetchContactsTable())
    }

    const contactsTableFormatted = useMemo(() => {
        setDataLoaded(true)
        return contactsTable.map(o => ({
            ...o,
            nullValue: o.isUser ? <i className='material-icons small' style={{ lineHeight: '19px' }}>person</i> : '',
        }))
    }, [contactsTable])

    const internationalContext = useMemo(() => applicationSettings.find(a => a.parameter === 'ContexteInternational'), [applicationSettings])
    const isInternationalContext = useMemo(() => internationalContext ? internationalContext.value.toLowerCase() === 'oui' : false, [internationalContext])
    const countryCodeHeader = useMemo(() => isInternationalContext ? ['country'] : [], [isInternationalContext])
    const headers = useMemo(() => ['nullValue', 'name', 'postalCode', 'city', 'phoneTel', 'desktopTel', 'mobile', 'email', 'socialReason', ...countryCodeHeader], [countryCodeHeader])

    const exportData = useMemo(() => getDataWithHeaders(contactsTableFormatted, headers), [contactsTableFormatted, headers])

    useActions(() => {
        return {
            new: () => dispatch(push(rpqs ? `${PATH_REFERENCIAL_RPQS_LINK}contact/new` : PATH_REFERENCIAL_CONTACT_NEW)),
            referencialActions: {
                actions: {
                    export: {
                        data: getDataWithHeaders(exportData, headers),
                        titleFile: i18n.contacts,
                    },
                    purge: {
                        onPurge: () => dispatch(ContactAction.purgeContact()),
                    },
                },
            },
        }
    }, [exportData, rpqs])

    return (
        <Stack spacing={1} direction='column' margin={1}>
            <Card>
                <CardTitle title={i18n.criterias} color={'bannerColor'} />
                <CardContent>
                    <Grid container spacing={1}>
                        <Grid size={8}>
                            <Input
                                title={ i18n.search }
                                value={ filter.searchValue }
                                onChange={ (value) => setFilter({ ...filter, searchValue: value }) }
                                onEnterKeyPress={fetchData}
                            />
                        </Grid>
                        <Grid offset={2} />
                        {isInternationalContext && (
                            <Grid size={3}>
                                <Select
                                    label={i18n.country}
                                    options={countries}
                                    value={filter.countrySelected}
                                    keyValue='countryCode'
                                    keyLabel='name'
                                    onChange={value => setFilter({ ...filter, countrySelected: value })}
                                />
                            </Grid>
                        )}
                        <Grid size={isInternationalContext ? 3 : 4}>
                            <Select
                                options={ cities }
                                label={ i18n.city }
                                onChange={(_, value) => setFilter({ ...filter, citySelected: value })}
                                value={ filter.citySelected?.id }
                            />
                        </Grid>
                        <Grid offset={7}/>
                        <Grid container spacing={1} size={6}>
                            <Grid size={3}>
                                <Checkbox
                                    label={i18n.admin}
                                    checked={filter.isAdmin}
                                    onChange={value => setFilter({ ...filter, isAdmin: value })}
                                />
                            </Grid>
                            <Grid size={3}>
                                <Checkbox
                                    label={i18n.administrator}
                                    checked={filter.metadata}
                                    onChange={value => setFilter({ ...filter, metadata: value })}
                                />
                            </Grid>
                            <Grid size={3}>
                                <Checkbox
                                    label={i18n.technicien}
                                    checked={filter.labo}
                                    onChange={value => setFilter({ ...filter, labo: value })}
                                />
                            </Grid>
                            <Grid size={3}>
                                <Checkbox
                                    label={i18n.consultant}
                                    checked={filter.consultant}
                                    onChange={value => setFilter({ ...filter, consultant: value })}
                                />
                            </Grid>
                        </Grid>
                        <Grid size={6} container spacing={1} justifyContent='flex-end'>
                            <Button variant='outlined' onClick={onResetFilters}>{i18n.reinit}</Button>
                            <Button variant='contained' onClick={fetchData}>{i18n.search}</Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {dataLoaded ? (
                <CardTable
                    color={'bannerColor'}
                    title={ i18n.contactsList }
                    rows={contactsTable}
                    rowsPerPageOptions={nbPerPageLabel}
                    headers={headers}
                    onClickRow={(e) => {
                        dispatch(push(rpqs ? `${PATH_REFERENCIAL_RPQS_LINK}contact/${e.id}/dashboard` : `/${LINK_REFERENCIAL_CONTACT}/${e.id}/dashboard`))
                    }}
                />
            ) : (
                <ProgressBar indeterminate />
            )}
        </Stack>
    )
}

ContactsPanel.propTypes = {
    rpqs: PropTypes.bool,
}

const ContactsApp = ({ rpqs }) => (
    <Tabs
        defaultTab={CONTACT}
        tabs={[
            {
                constant: CONTACT,
                label: i18n.contacts,
            },
            {
                constant: GROUP,
                label: i18n.contactsGroups,
            },
            {
                constant: MAP,
                label: i18n.map,
            },
        ]}
    >
        {tab => (
            <>
                {tab === CONTACT && (<ContactsPanel rpqs={rpqs} />)}
                {tab === GROUP && (<ContactsGroupsApp rpqs={rpqs} />)}
                {tab === MAP && (<ContactMapPanel rpqs={rpqs} />)}
            </>
        )}
    </Tabs>
)

ContactsApp.propTypes = {
    rpqs: PropTypes.bool,
}

export default ContactsApp
